import { useCurrentUser } from "@/shared/auth/useAuth";
import { Permission } from "@/shared/auth/permissions";
import { usePermission } from "@/shared/auth/usePermission";
import { Role } from "@/shared/types/role";

export interface UserDetailCapabilities {
  /** Account management (deactivate, reactivate, legacy role, password reset, test flag, delete). */
  readonly canManage: boolean;
  readonly canNotify: boolean;
  readonly canVerifyPhone: boolean;
  /** Whether the viewer may call the founding-account read (ADMIN only here). */
  readonly canReadFounding: boolean;
  /** Whether the viewer may follow the owner payout history link (ADMIN only here). */
  readonly canReadPayouts: boolean;
  readonly canReadMemberships: boolean;
  readonly canManageMemberships: boolean;
  readonly canChangeMemberRoles: boolean;
  readonly canManageOfflineAcceptances: boolean;
  readonly currentAdminFirebaseUid: string | null;
}

/**
 * The viewer-side gates of the user detail page in one place. Permission
 * gates come from the session permissions (ADR-0017, fail closed); the
 * remaining role literals mirror the API's role gates and are tracked for
 * the permission migration (CLAUDE.md, "Access model").
 */
export function useUserDetailCapabilities(): UserDetailCapabilities {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.role === Role.WANDERFIT_ADMIN;
  const isSupport = currentUser?.role === Role.WANDERFIT_SUPPORT;
  return {
    canManage: isAdmin,
    // Ad-hoc notifications are an admin + support tool (matches the
    // `POST /api/admin/notifications` role gate). Finance cannot send.
    canNotify: isAdmin || isSupport,
    // Manual phone verification is an admin + support tool (matches the
    // `POST /api/admin/users/:id/phone/verify` role gate). Finance cannot.
    canVerifyPhone: isAdmin || isSupport,
    // Founding Partner badge + section (ADR-0010 §9). The founding-account read
    // is ADMIN + FINANCE on the API, but this page is ADMIN + SUPPORT
    // (`SECTION_ROLES.users`, like `GET /admin/users/:id`), so the only viewer
    // that can both be here and read it is ADMIN — SUPPORT (who would 403)
    // never triggers the read.
    canReadFounding: isAdmin,
    // Owner payout history lives in the finance slice (read tier ADMIN +
    // FINANCE); of the roles that can open this page only ADMIN may follow it.
    canReadPayouts: isAdmin,
    canReadMemberships: usePermission(Permission.ORG_MEMBERS_READ),
    canManageMemberships: usePermission(Permission.ORG_MEMBERS_MANAGE),
    canChangeMemberRoles: usePermission(Permission.ORG_ADMINS_MANAGE),
    // Offline ToS acceptances: gated on the API permission, not on a role literal.
    canManageOfflineAcceptances: usePermission(Permission.TOS_OFFLINE_MANAGE),
    currentAdminFirebaseUid: currentUser?.uid ?? null,
  };
}
